/**
 * Autopilot(自動開發部署系統)API 封裝。
 *
 * 主系統 sonice-inner-system 直接提供 /api/autopilot/* —— online-schedule 是掛在主系統
 * /project 底下的內嵌子系統(跟 autopilot-system 本身一樣的機制),同源、session cookie 共用,
 * 直接打相對路徑即可,不需要另外接 bearer token(跟 specforge/Artemis 那條跨服務的整合不同)。
 */

async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { data: null, error: data.error || `請求失敗(${res.status})` };
  return { data, error: null };
}

/**
 * 送出「AI 修復」需求(spec = 該工作項目內容,由呼叫端組好傳入)。
 * previewRole:送修時選的「以哪個角色預覽」(preview_test_accounts.id),fix 修完自動截圖用;
 * 免登入 / 未選則不帶。
 */
export const createAutopilotRequest = async ({ pmsWorkItemId, spec, previewRole }) => {
  const res = await fetch('/api/autopilot/requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pmsWorkItemId, spec, previewRole }),
  });
  return parseJson(res);
};

/** 列出某專案登記的預覽測試帳號(不含密碼),供送修角色下拉 + 設定頁登記清單用。 */
export const listPreviewAccounts = async (projectId) => {
  const res = await fetch(`/api/autopilot/preview-accounts?projectId=${encodeURIComponent(projectId)}`);
  return parseJson(res);
};

/** 登記一組預覽測試帳號。params = 非機密參數(account/company…),secretParams = 機密(password…)。 */
export const createPreviewAccount = async ({ projectId, label, role, params, secretParams }) => {
  const res = await fetch('/api/autopilot/preview-accounts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, label, role, params, secretParams }),
  });
  return parseJson(res);
};

/** 刪除(軟刪)一組預覽測試帳號。 */
export const deletePreviewAccount = async (id) => {
  const res = await fetch(`/api/autopilot/preview-accounts?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
  return parseJson(res);
};

/** 查某工作項目目前(最新一筆)AI 修復需求。沒送過回 null。 */
export const getAutopilotRequestForWorkItem = async (pmsWorkItemId) => {
  const res = await fetch(`/api/autopilot/requests?pmsWorkItemId=${encodeURIComponent(pmsWorkItemId)}&page=1`);
  const { data, error } = await parseJson(res);
  if (error) return { data: null, error };
  return { data: data.items?.[0] || null, error: null };
};

/** 讀某需求的討論串(原始需求後的每則補充說明),依時間排序。 */
export const getAutopilotMessages = async (requestId) => {
  const res = await fetch(`/api/autopilot/requests/${requestId}/discuss`);
  return parseJson(res);
};

/** 追加討論(同分支重跑,diff 累積)。 */
export const discussAutopilotRequest = async (requestId, text) => {
  const res = await fetch(`/api/autopilot/requests/${requestId}/discuss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  return parseJson(res);
};

/** 讀該需求目前真實狀態(pipeline 進度)。 */
export const getAutopilotStatus = async ({ projectPath, pipelineId, branch }) => {
  const params = new URLSearchParams({ projectId: projectPath, pipelineId: String(pipelineId) });
  if (branch) params.set('branch', branch);
  const res = await fetch(`/api/autopilot/status?${params.toString()}`);
  return parseJson(res);
};

/**
 * 該專案 AI 修復的歷史紀錄(部署 modal 用),依 project_path 篩,不分頁(給前端按部署分組收合)。
 * 回傳 { items, deployments } —— items.deployment_id 對應 deployments 裡的一筆,null = 還沒被部署帶走。
 */
export const getAutopilotHistory = async (projectPath) => {
  const res = await fetch(`/api/autopilot/requests?projectPath=${encodeURIComponent(projectPath)}`);
  return parseJson(res);
};

/**
 * 新專案第一次掛 Autopilot 的一次性佈署(commit CI 範本進目標 repo + 設 DEPLOY_PLATFORM)。
 * 冪等,存 autopilot_config 時每次都呼叫沒關係,不用自己判斷是不是第一次設定。
 */
export const provisionAutopilot = async (gitlabProjectPath, platform) => {
  const res = await fetch('/api/autopilot/provision', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gitlabProjectPath, platform }),
  });
  return parseJson(res);
};

/**
 * 部署到 staging(後端先 bump 版本號 commit 回 main,再觸發部署 pipeline;該專案已 committed
 * 的需求全部標成已部署)。只有 staging——prod 是另一套人工流程,Autopilot 不碰。
 * markOnly:不部署,只把目前 main HEAD 校正成基準線(在別處手動部署完之後用)。
 */
export const deployAutopilot = async (projectPath, { markOnly, versionFile } = {}) => {
  const res = await fetch('/api/autopilot/deploy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId: projectPath, markOnly, versionFile }),
  });
  return parseJson(res);
};
